import { useEffect, useState } from "react";

import { useBuyerSupplierAwardStats } from "lib/hooks/api/buyer/useBuyerSupplierAwardStats";
import { supplierSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { useSpendTransactions } from "lib/hooks/api/spend/useSpendTransactions";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { BuyerSupplierRange, rangeStart } from "../BuyerSupplierViewControls";

// Longest last: the first that reaches back to the pair's latest activity wins. Never narrower
// than the page's default, so a busy pair still opens on a few years of pattern.
const FITTING_RANGES: BuyerSupplierRange[] = ["P-3Y", "P-5Y", "all"];

/**
 * The shortest range, from three years up, that includes `latest` (an ISO date). No activity at
 * all keeps the default: there's nothing to reveal, and the empty state says so.
 */
export function fitRange(latest: string | undefined): BuyerSupplierRange {
  if (!latest) {
    return "P-3Y";
  }
  const day = latest.slice(0, 10);
  return FITTING_RANGES.find((range) => (rangeStart(range) ?? "") <= day) ?? "all";
}

/** The later of two ISO dates, ignoring missing ones. */
function later(a: string | null | undefined, b: string | null | undefined): string | undefined {
  if (!a) return b ?? undefined;
  if (!b) return a;
  return a.slice(0, 10) >= b.slice(0, 10) ? a : b;
}

type Options = {
  buyerGuid: string;
  supplierGuid: string;
  hasSpendAccess: boolean;
  supplierMentionType: SupplierMentionType;
  onFit: (range: BuyerSupplierRange) => void;
};

/**
 * Opens the page on a range that shows the pair's latest award or invoice, so following a link to
 * an old relationship doesn't land on an empty page. Only when the URL doesn't already name a
 * range: a range someone picked, or a shared link, is left alone.
 */
export function useFittedRange({
  buyerGuid,
  supplierGuid,
  hasSpendAccess,
  supplierMentionType,
  onFit,
}: Options): { isFitting: boolean } {
  // Read once on arrival; after that the range is the user's.
  const [needsFit] = useState(() => !new URLSearchParams(window.location.search).has("range"));
  const [isFitted, setIsFitted] = useState(false);
  const pending = needsFit && !isFitted;

  // All time, so the latest dates aren't cut off by the range being chosen.
  const awards = useBuyerSupplierAwardStats(
    buyerGuid,
    supplierGuid,
    supplierMentionType,
    undefined,
    // No retries: a failed lookup should fall back to the default straight away, not after
    // react-query's backoff holds the spinner up for several seconds.
    { enabled: pending, retry: false },
  );
  // Transactions come back newest first, so the first row is the latest invoice.
  const spend = useSpendTransactions(
    { filters: supplierSpendFilter({ buyerGuid, supplierGuid }), limit: 1 },
    // The API refuses spend without the entitlement, so don't ask.
    { enabled: pending && hasSpendAccess, retry: false },
  );

  const awardsDone = !awards.isLoading || awards.isError;
  const spendDone = !hasSpendAccess || !spend.isLoading || spend.isError;

  useEffect(() => {
    if (!pending || !awardsDone || !spendDone) {
      return;
    }
    // A failed lookup keeps the default rather than holding the page up.
    const latest = later(awards.data?.latestAwardDate, spend.data?.results[0]?.date);
    onFit(fitRange(latest));
    setIsFitted(true);
  }, [pending, awardsDone, spendDone, awards.data, spend.data, onFit]);

  return { isFitting: pending };
}
